import { createRouter, createWebHistory } from 'vue-router'
import { authGuard } from './authGuard'
import { applyRouteSeo, PRICE_TAGLINE } from '@/utils/seo'
import { LP_ENTRIES } from '@/views/lp/registry'
import { installNavDirection } from '@/motion/navDirection'
import { isStaleChunkError, reloadForStaleChunk } from '@/utils/staleChunk'
import { openPublicPage } from '@/native/publicPages'
// Ambient module augmentation for route meta SEO fields — picked up
// automatically via tsconfig `include`, no runtime import needed.
// See src/router/route-meta.d.ts

/**
 * Marketing pages are unreachable in the native app (authGuard sends their names to login or the dashboard), so
 * the native build (`__LH_NATIVE__`, vite.config.mts) compiles their code, images and videos out: the route stays
 * (so the guard still sees its name) with an empty component.
 */
const NativeExcluded = { render: () => null }

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior(to, _from, savedPosition) {
        if (to.hash) {
            // Conversation artifact deep links use `#artifact:<uuid>`; the colon
            // is invalid in CSS id selectors and is handled in ConversationSurface.
            if (to.hash.startsWith('#artifact:')) return false
            return new Promise((resolve) => {
                setTimeout(() => {
                    const id = to.hash.slice(1)
                    resolve({ el: `#${CSS.escape(id)}`, behavior: 'smooth' })
                }, 150)
            })
        }
        if (to.meta.scrollToTop) return { top: 0, left: 0 }
        if (savedPosition) return savedPosition
        return { top: 0, left: 0 }
    },
    routes: [
        {
            path: '/',
            name: 'home',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/Landing.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Lighthouse | The AI Assistant for Your Brand Deals',
                description:
                    'Lighthouse replies to brand emails in your voice, reviews contracts, tracks every project, and chases invoices — so you can run your creator business without a manager.' + PRICE_TAGLINE,
            },
            strict: true,
        },
        {
            // Shared public links. Same landing as / — no special trial offer.
            // Canonicalizes to `/` so link equity and indexing consolidate there.
            path: '/launch',
            name: 'launch',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/Landing.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: __LH_NATIVE__ ? 'Lighthouse' : 'Start your free trial',
                description:
                    __LH_NATIVE__
                    ? 'Lighthouse, the AI assistant that runs your brand deals.'
                    : 'Start a free trial of Lighthouse, the AI assistant that runs your brand deals: emails, contracts, projects, and invoices.' + PRICE_TAGLINE,
                canonicalPath: '/',
            },
            strict: true,
        },
        {
            path: '/launch/signup',
            name: 'launch-signup',
            redirect: { path: '/launch', query: { signup: 'true' } },
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Start your free trial',
                noindex: true,
            },
            strict: true,
        },
        {
            // Public no-auth guest walkthrough. Hard-navigate here so GUEST_MODE
            // is true for the whole page load (see src/guest/guestMode.ts).
            path: '/tour',
            name: 'guest-tour',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/GuestTour.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Product Tour',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/early-access',
            redirect: (to) => ({ path: '/', query: to.query }),
        },
        {
            path: '/early-access/signup',
            redirect: (to) => ({
                path: '/',
                query: { ...to.query, signup: 'true' },
            }),
        },
        {
            path: '/login',
            name: 'login',
            redirect: { path: '/', query: { login: 'true' } },
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Login',
                noindex: true,
            },
            strict: true,
        },
        {
            // Native-app-only login entry (no marketing landing page in the
            // native v1 scope). See router/authGuard.ts.
            path: '/native-login',
            name: 'native-login',
            component: () => import('@/views/NativeLogin.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Log in | Lighthouse',
                noindex: true,
            },
            strict: true,
        },
        {
            // HTTPS OAuth redirect target for the native app (Universal Links /
            // App Links). When association works, the OS opens the app and this
            // page never loads; otherwise it forwards ?code to /dashboard.
            path: '/oauth-callback',
            name: 'oauth-callback',
            component: () => import('@/views/OAuthCallback.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Connecting…',
                noindex: true,
            },
            strict: true,
        },
        {
            // Supabase Auth Google login bridge for the native app. Supabase
            // redirects here with ?code=; the page forwards onto the custom
            // URL scheme (or Universal Links open the app directly).
            path: '/auth/callback',
            name: 'auth-native-callback',
            component: () => import('@/views/AuthNativeCallback.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Signing in…',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/netscape',
            name: 'netscape',
            component: () => import('@/views/NetscapeSimulator.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Netscape',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/dashboard/projects/:projectId/deliverables/:deliverableId',
            name: 'project-deliverable',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Deliverable',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/dashboard/projects/:projectId',
            name: 'project',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Project',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/dashboard/invoices/:invoiceId',
            name: 'invoice-editor',
            component: () => import('@/views/InvoiceEditorPage.vue'),
            meta: {
                authRequired: true,
                layout: 'AppLayoutApp',
                title: 'Invoice',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/dashboard/agreements/:agreementId',
            name: 'agreement-review',
            component: () => import('@/views/AgreementReview.vue'),
            meta: {
                authRequired: true,
                layout: 'AppLayoutApp',
                title: 'Agreement Review | Lighthouse',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/dashboard/conversations',
            name: 'conversations',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Conversations',
                noindex: true,
            },
            strict: true,
        },
        {
            // The agent's scheduled tasks (standing goals), opened from the conversations sidebar.
            path: '/dashboard/conversations/scheduled/:goalId?',
            name: 'scheduled',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                authRequired: true,
                layout: 'AppLayoutApp',
                title: 'Scheduled tasks',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/dashboard/conversations/:conversationId',
            name: 'conversation',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Conversation',
                noindex: true,
            },
            strict: true,
        },
        {
            // Meeting notes from the desktop app (the tab itself is in Dashboard.vue).
            path: '/dashboard/meetings',
            name: 'meetings',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Meetings',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/dashboard/meetings/:meetingId',
            name: 'meeting',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Meeting',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/dashboard/marketing',
            redirect: '/dashboard/pitching',
            strict: true,
        },
        {
            path: '/dashboard/:section?/:option?',
            name: 'dashboard',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Dashboard',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/impersonate',
            name: 'impersonate',
            component: () => import('@/views/ImpersonateEntry.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Impersonate',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/verify',
            name: 'verify',
            component: () => import('@/views/Verify.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Verify',
                noindex: true,
            },
            strict: true,
        },
        {
            // One-time link from a landing setup-link email (Meta ads / Instagram browser).
            path: '/setup/:token?',
            name: 'setup-link',
            component: () => import('@/views/SetupLink.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Set up Lighthouse',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/reset-password',
            name: 'reset-password',
            component: () => import('@/views/ResetPassword.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Reset Password',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/logout',
            name: 'logout',
            component: () => import('@/views/LogOut.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Logout',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/privacy',
            name: 'privacy',
            component: () => import('@/views/PrivacyPolicy.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Privacy Policy | Lighthouse',
                description: "Lighthouse's privacy policy — how we collect, use, and protect your data.",
            },
            strict: true,
        },
        {
            path: '/terms',
            name: 'terms',
            // The terms quote the web subscription price, which the store apps may not show, so the native build
            // compiles the page out and opens the public page in the in-app browser instead.
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/TermsOfService.vue'),
            beforeEnter: __LH_NATIVE__
                ? () => {
                      void openPublicPage('terms')
                      return false
                  }
                : undefined,
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Terms of Service | Lighthouse',
                description: "Lighthouse's terms of service.",
            },
            strict: true,
        },
        {
            path: '/support',
            name: 'support',
            component: () => import('@/views/SupportPage.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Support | Lighthouse',
                description: 'Get help with Lighthouse: contact support and answers to common questions.',
            },
            strict: true,
        },
        {
            // Google Play's account deletion URL: works without the app.
            path: '/delete-account',
            name: 'delete-account',
            component: () => import('@/views/DeleteAccountPage.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Delete your account | Lighthouse',
                description: 'How to delete your Lighthouse account, what is deleted and what is kept.',
            },
            strict: true,
        },
        {
            path: '/content',
            name: 'content',
            component: () => import('@/views/ContentReview.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Content',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/media-kit/:slug/case-study/:caseStudyId',
            name: 'media-kit-case-study',
            component: () => import('@/views/MediaKitCaseStudy.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Case Study',
                description: 'A brand partnership case study, built with Lighthouse.',
            },
            strict: true,
        },
        {
            path: '/media-kit/:slug',
            name: 'media-kit',
            component: () => import('@/views/MediaKitLink.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Media Kit',
                description: 'A creator media kit, built with Lighthouse.',
            },
            strict: true,
        },
        {
            path: '/c/:creator_link',
            name: 'creator',
            component: () => import('@/views/CreatorLink.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Creator',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/draft/:shareToken',
            name: 'draft-share',
            component: () => import('@/views/DraftShare.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Draft review',
                // Unguessable per-brand share token — never indexable.
                noindex: true,
            },
            strict: true,
        },
        {
            // Accept a workspace invite. Public: signed-out visitors sign in or sign up on the page.
            path: '/join/:token',
            name: 'join-workspace',
            component: () => import('@/views/JoinWorkspace.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Join a workspace | Lighthouse',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/changelog',
            name: 'changelog',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/Changelog.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Changelog | Lighthouse',
                description: 'Whats new in Lighthouse — the latest features and improvements to your AI creator assistant.',
            },
            strict: true,
        },
        {
            path: '/mobile',
            name: 'mobile',
            component: () => import('@/views/MobileGuide.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Lighthouse on your phone | Lighthouse',
                description: 'Add Lighthouse to your home screen on iPhone or Android and turn on push notifications.',
            },
            strict: true,
        },
        {
            path: '/updates',
            name: 'updates',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/UpdatesIndex.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Product Updates | Lighthouse',
                description: 'Product updates and release notes from the Lighthouse team.',
            },
            strict: true,
        },
        {
            path: '/updates/:slug',
            name: 'update-detail',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/UpdateDetail.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Product Update | Lighthouse',
                description: 'A Lighthouse product update.',
                ogType: 'article',
            },
            strict: true,
        },
        {
            path: '/unsubscribe',
            name: 'unsubscribe',
            component: () => import('@/views/Unsubscribe.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Unsubscribe | Lighthouse',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/pricing-guide',
            name: 'pricing-guide',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/PricingGuideView.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Brand Deal Pricing Guide | Lighthouse',
                description:
                    'A free guide to pricing your brand deals in 2026 — rate tables, usage & whitelisting fees, and negotiation scripts for creators.',
            },
            strict: true,
        },
        {
            path: '/our-story',
            name: 'our-story',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/OurStory.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Our Story | Lighthouse',
                description: 'The origin story of two brothers turned co-founders building AI for the creator economy.',
                ogImage: new URL('../assets/images/kevinandryan.png', import.meta.url).href,
            },
            strict: true,
        },
        {
            path: '/press',
            name: 'press',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/PressRelease.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Press Release | Lighthouse',
                description: 'Two brothers launch an AI creator assistant with a short film instead of a product demo.',
                ogType: 'article',
            },
            strict: true,
        },
        {
            path: '/demo/brand-portal',
            name: 'demo-brand-portal',
            component: () => import('@/views/DemoBrandPortal.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Brand Partner Portal | Bloom Skincare',
                noindex: true,
            },
            strict: true,
        },
        {
            // Shareable referral link. Stores the code, then redirects to the landing page.
            path: '/r/:code',
            name: 'referral-capture',
            component: () => import('@/views/ReferralCapture.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Lighthouse',
            },
            strict: true,
        },
        {
            path: '/referrals',
            name: 'referrals',
            component: () => import('@/views/ReferralPortal.vue'),
            meta: {
                authRequired: true,
                layout: 'AppLayoutApp',
                title: 'Refer & Earn | Lighthouse',
            },
            strict: true,
        },
        {
            // Partner program landing: businesses (agencies, coaches,
            // consultancies) apply here. Creators apply at /ambassador.
            path: '/partners',
            name: 'partners-landing',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/PartnersLanding.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Partner Program | Lighthouse',
                description: 'Earn 20% recurring commission by introducing the creators you work with to Lighthouse. For agencies, managers, coaches and consultancies.',
            },
            strict: true,
        },
        {
            // Standalone partner portal for ambassadors and partners without a
            // Lighthouse account. Its own session (partners/partnerSupabase.ts),
            // so these routes are public to the product's auth guard.
            path: '/partners/portal',
            name: 'partner-portal',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/partners/PartnerPortal.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Partner Portal | Lighthouse',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/partners/login',
            name: 'partner-login',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/partners/PartnerSignIn.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Partner Sign In | Lighthouse',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/partners/auth',
            name: 'partner-auth',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/partners/PartnerSignIn.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Partner Sign In | Lighthouse',
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/ambassador',
            name: 'ambassador',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/Ambassador.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Ambassador Program | Lighthouse',
                description: 'Join the Lighthouse Ambassador Program and help other creators discover the future of creator management.',
            },
            strict: true,
        },
        {
            path: '/inbox',
            name: 'inbox-landing',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/InboxLanding.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'The Inbox Built for Creators | Lighthouse',
                description: 'Your inbox, supercharged with AI that drafts replies to brands, tracks pitches, and never misses a deal.',
            },
            strict: true,
        },
        {
            path: '/deals',
            name: 'deals-landing',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/DealsLanding.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'The CRM Built for Creators | Lighthouse',
                description: 'Every brand deal, on autopilot from first email to final payment. The CRM built for creators.',
            },
            strict: true,
        },
        {
            path: '/assistant',
            name: 'assistant-landing',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/AssistantLanding.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Your AI Business Partner | Lighthouse',
                description: 'Ask anything, get it done — drafts, invoices, answers, instantly. Meet your AI business partner.',
            },
            strict: true,
        },
        {
            // Internal keynote for the harness-v2 release (not linked).
            path: '/keynote',
            name: 'keynote',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/keynote/KeynoteDeck.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Lighthouse, rebuilt · Keynote',
                noindex: true,
            },
            strict: true,
        },
        {
            // Index of the /lp conversion-test variants (internal, not linked).
            path: '/lp',
            name: 'lp-index',
            component: __LH_NATIVE__ ? NativeExcluded : () => import('@/views/lp/LpIndex.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Landing variants',
                noindex: true,
            },
            strict: true,
        },
        {
            // The former /lp/remastered variant is now the main landing.
            path: '/lp/remastered',
            redirect: (to) => ({ path: '/', query: to.query, hash: to.hash }),
        },
        ...(__LH_NATIVE__ ? [] : LP_ENTRIES).map((entry) => ({
            path: `/lp/${entry.slug}`,
            name: `lp-${entry.slug}`,
            component: entry.component,
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: entry.title,
                description: entry.description,
                canonicalPath: '/',
                noindex: true,
            },
            strict: true,
        })),
        {
            // Campaign links sometimes gain a trailing slash; don't 404 them.
            path: '/lp/:slug?/',
            redirect: (to) => ({
                path: to.params.slug ? `/lp/${String(to.params.slug)}` : '/lp',
                query: to.query,
                hash: to.hash,
            }),
            strict: true,
        },
        {
            path: '/early-access/:token',
            redirect: (to) => ({
                path: '/',
                query: { ...to.query, l: String(to.params.token || '') },
            }),
        },
        {
            // Agent v2 run traces (staff): the Agent runs tab of the dev harness.
            path: '/dashboard/dev/agent-runs',
            redirect: (to) => ({ name: 'dev-testing', query: { ...to.query, tab: 'agent-runs' } }),
        },
        {
            path: '/dev/testing',
            name: 'dev-testing',
            component: () => import('@/views/DevTestingHarness.vue'),
            meta: {
                authRequired: true,
                layout: 'AppLayoutApp',
                title: 'Inbox Agent Dev Harness',
                // Enforced in authGuard via canAccessDevHarness(email).
                requireDevHarnessAccess: true,
                noindex: true,
            },
            strict: true,
        },
        {
            path: '/404',
            name: 'not-found',
            component: () => import('@/views/NotFound.vue'),
            meta: {
                authRequired: false,
                layout: 'AppLayoutApp',
                title: 'Page Not Found | Lighthouse',
                noindex: true,
            },
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/404',
        },
        // REMEMBER: When adding any new routes, be sure to include them in vercel.json as well.
    ],
})

router.beforeEach(authGuard)
// Push / pop / replace for transitions (tab cross-fades vs. stack slides).
installNavDirection(router)

router.afterEach((to) => {
    applyRouteSeo(to.fullPath, {
        title: to.meta.title,
        description: to.meta.description,
        ogImage: to.meta.ogImage,
        ogType: to.meta.ogType,
        canonicalPath: to.meta.canonicalPath,
        // Dashboard/app routes are dynamic per-user and shouldn't be indexed
        // even if a meta.noindex was left unset by mistake — belt and
        // suspenders alongside the explicit per-route flags above.
        noindex: to.meta.noindex ?? to.path.startsWith('/dashboard'),
    })
})

router.onError((err, to) => {
    if (isStaleChunkError(err) && reloadForStaleChunk(to?.fullPath)) return

    console.error('router error', err)
    router.push('/404')
})

export default router
